import { API_BASE_URL, AUTH_BASE_URL, CATALOG_BASE_URL } from "../props";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import {
  useGet,
  doGetAll,
  usePut,
  usePost,
  usePostWithoutLocation,
  useDelete,
  useUpload,
} from "../common/fetchServices";
import { find, pickBy, propEq } from "ramda";
import { notBlank } from "../util/ext";
import querystring from "querystring";
import { useAuthenticationCheck } from "./useAuthenticationCheck";
import { useCurrentOrgId } from "../auth/authUtils";

const LIMIT = 10;

const getOrgsUrl = () => `${AUTH_BASE_URL}/organizations`;
const getOrgsApiUrl = () => `${API_BASE_URL}/organizations`;

const getLibSalesAcctUrl = ({ id }) =>
  `${AUTH_BASE_URL}/library/${id}/sales-account`;

const getOrgAuthUrl = ({ id }) => `${AUTH_BASE_URL}/organizations/${id}`;
const getOrgApiUrl = ({ id }) => `${API_BASE_URL}/organizations/${id}`;

const getOrgRolesUrl = ({ orgId }) =>
  `${AUTH_BASE_URL}/organizations/${orgId}/roles`;
const getOrgRoleUrl = ({ orgId, role }) =>
  `${AUTH_BASE_URL}/organizations/${orgId}/roles/${role}`;
const getOrgNameUrl = ({ id }) => `${API_BASE_URL}/organizations/${id}/name`;
const getOrgPropKeyUrl = ({ orgId, propKey }) =>
  `${API_BASE_URL}/organizations/${orgId}/properties/${propKey}`;
const getOrgContentProviderUrl = ({ orgId, contentProviderId }) =>
  `${API_BASE_URL}/organizations/${orgId}/content-providers/${contentProviderId}`;

const getOrgAppUrl = ({ orgId, appId }) =>
  `${AUTH_BASE_URL}/organizations/${orgId}/applications/${appId}`;
const getOrgAppDetailUrl = ({ orgId, appId }) =>
  `${AUTH_BASE_URL}/organizations/${orgId}/applications/${appId}/detail`;

const getOrgAssociatedOrgsUrl = ({ orgId }) =>
  `${AUTH_BASE_URL}/organizations/${orgId}/associated-organizations`;
const getOrgAssociatedOrgUrl = ({ orgId, authAssocOrgId }) =>
  `${AUTH_BASE_URL}/organizations/${orgId}/associated-organizations/${authAssocOrgId}`;

const getOrgLocationUrl = ({ orgId }) =>
  `${AUTH_BASE_URL}/organizations/${orgId}/location`;

const getOrgLogoUrl = ({ id }) => `${API_BASE_URL}/organizations/${id}/logo`;

const getOrgKeystoreUrl = ({ orgId }) =>
  `${AUTH_BASE_URL}/organizations/${orgId}/secret_keystore`;
const getOrgKeystoreAccessKeyUrl = ({ orgId, accessKey }) =>
  `${AUTH_BASE_URL}/organizations/${orgId}/secret_keystore/${accessKey}`;

const getBuyingGroupUrl = ({ orgId, memberId }) =>
  `${AUTH_BASE_URL}/buying_groups/${orgId}/members/${memberId}`;

const getActiveModuleUrl = ({ orgId, moduleId }) =>
  `${API_BASE_URL}/admin/organizations/${orgId}/active-modules/${moduleId}`;
const getRequestCollectionMarcURL = () =>
  `${CATALOG_BASE_URL}/marc/collections`;

const useGetAll = (key, url, enabled = true) => {
  const handleNotAuthenticated = useAuthenticationCheck();
  const auth = useSelector(state => state.auth);

  return useQuery(
    key,
    ({ queryKey }) => {
      return doGetAll(auth)(url);
    },
    {
      onError: handleNotAuthenticated,
      enabled,
    },
  );
};

export const useOrganizationSearch = ({
  filter,
  order,
  page,
  limit = LIMIT,
  activeOnly = "false",
}) => {
  const offset = page * limit;
  const params = pickBy((val, _key) => notBlank(val), {
    filter,
    order,
    offset,
    limit,
    activeOnly,
  });
  const queryParams = querystring.encode(params);
  const url = `${AUTH_BASE_URL}/organizations?${queryParams}`;
  return useGet(["organizations", { url }], url);
};

export const useOrganizationsByRole = role => {
  const url = `${AUTH_BASE_URL}/roles/${role}/organizations`;
  return useGet(["organizations", { role }], url);
};

export const useOrganizationDetails = id => {
  const url = `${AUTH_BASE_URL}/organizations/${id}`;
  return useGet(["organization-details", { id }], url);
};

export const useOrganizationDetailsUpdate = () => {
  return usePut(getOrgAuthUrl, {
    queriesToInvalidate: [
      "organizations",
      "organizations-all",
      ["organizations-details", ["id"]],
    ],
  });
};

export const useOrganizationDetailsCreate = () => {
  return usePost(getOrgsUrl, {
    queriesToInvalidate: ["organizations", "organizations-all"],
  });
};

export const useLibrarySalesAccount = id => {
  const url = `${AUTH_BASE_URL}/library/${id}/sales-account`;
  return useGet(["sales-accounts", { id }], url);
};

export const useLibrarySalesAccountUpdate = () => {
  return usePut(getLibSalesAcctUrl, {
    queriesToInvalidate: [
      "organizations",
      "organizations-all",
      ["sales-accounts", ["id"]],
    ],
  });
};

export const useLibrarySalesAccountCreate = () => {
  return usePost(getLibSalesAcctUrl, {
    queriesToInvalidate: ["organizations", "organizations-all"],
  });
};

export const useOrganizationProfile = id => {
  const url = `${API_BASE_URL}/organizations/${id}`;
  return useGet(["organization-profile", { id }], url);
};

export const useOrganizationProfileUpdate = () => {
  return usePut(getOrgApiUrl, {
    queriesToInvalidate: [
      "organizations",
      "organizations-all",
      ["organization-profile", ["id"]],
    ],
  });
};

export const useOrganizationProfileCreate = () => {
  return usePost(getOrgsApiUrl, {
    queriesToInvalidate: ["organizations", "organizations-all"],
  });
};

export const useOrganizationProfileNameUpdate = () => {
  return usePut(getOrgNameUrl, {
    queriesToInvalidate: [
      "organizations",
      "organizations-all",
      ["organization-profile", ["id"]],
    ],
  });
};

export const useOrganizationProperties = id => {
  const url = `${API_BASE_URL}/organizations/${id}/properties`;
  return useGet(["organization-properties", { id }], url);
};

export const useOrganizationPropertyKeys = () => {
  const url = `${API_BASE_URL}/organizations/properties/keys`;
  return useGet("organization-property-keys", url);
};

export const useOrganizationPropertyKeyValues = propKey => {
  const url = `${API_BASE_URL}/organizations/properties/keys/${propKey}`;
  return useGet(["organization-property-values", { propKey }], url, !!propKey);
};

export const useOrganizationPropertyCreate = () => {
  return usePut(getOrgPropKeyUrl, {
    queriesToInvalidate: ["organization-properties"],
  });
};

export const useOrganizationPropertyDelete = () => {
  return useDelete(getOrgPropKeyUrl, {
    queriesToInvalidate: ["organization-properties"],
  });
};

export const useOrganizationContentProviders = id => {
  const url = `${API_BASE_URL}/organizations/${id}/content-providers`;
  return useGet(["organization-content-providers", { id }], url);
};

export const useContentProviderCreate = () => {
  return usePut(getOrgContentProviderUrl, {
    queriesToInvalidate: ["organization-content-providers"],
  });
};

export const useContentProviderDelete = () => {
  return useDelete(getOrgContentProviderUrl, {
    queriesToInvalidate: ["organization-content-providers"],
  });
};

export const useOrganizationApplications = id => {
  const url = `${AUTH_BASE_URL}/organizations/${id}/applications`;
  return useGet(["organization-applications", { orgId: id }], url);
};

export const useOrganizationApplicationCreate = () => {
  return usePut(getOrgAppUrl, {
    queriesToInvalidate: ["organization-applications", ["orgId"]],
  });
};

export const useOrganizationApplicationDelete = () => {
  return useDelete(getOrgAppUrl, {
    queriesToInvalidate: ["organization-applications", ["orgId"]],
  });
};

export const useOrganizationApplicationDetail = (orgId, appId) => {
  const url = `${AUTH_BASE_URL}/organizations/${orgId}/applications/${appId}/detail`;
  return useGet(
    ["organization-application-detail", { orgId, appId }],
    url,
    notBlank(orgId) && notBlank(appId),
  );
};

export const useOrganizationApplicationDetailCreate = () => {
  return usePost(getOrgAppDetailUrl, {
    queriesToInvalidate: [
      "organization-application-detail",
      ["orgId", "appId"],
    ],
  });
};

export const useOrganizationApplicationDetailUpdate = () => {
  return usePut(getOrgAppDetailUrl, {
    queriesToInvalidate: [
      "organization-application-detail",
      ["orgId", "appId"],
    ],
  });
};

export const useOrganizationRoles = id => {
  const url = `${AUTH_BASE_URL}/organizations/${id}/roles`;
  return useGet(["organization-roles", { id }], url);
};

export const useOrganizationRolesAdd = () => {
  return usePostWithoutLocation(getOrgRolesUrl, {
    queriesToInvalidate: ["organization-roles"],
  });
};

export const useOrganizationRoleDelete = () => {
  return useDelete(getOrgRoleUrl, {
    queriesToInvalidate: ["organization-roles"],
  });
};

export const useOrganizationAssociations = id => {
  const url = `${AUTH_BASE_URL}/organizations/${id}/associated-organizations`;
  return useGet(["organization-associations", { id }], url);
};

export const useAssociatedOrganizationCreate = () => {
  return usePost(getOrgAssociatedOrgsUrl, {
    queriesToInvalidate: ["organization-associations"],
  });
};

export const useAssociatedOrganizationDelete = () => {
  return useDelete(getOrgAssociatedOrgUrl, {
    queriesToInvalidate: ["organization-associations"],
  });
};

export const useCredentialTypes = () => {
  const url = `${AUTH_BASE_URL}/authentication/credential-types`;
  return useGet("credential-types", url);
};

export const useOrganizationLocation = id => {
  const url = `${AUTH_BASE_URL}/organizations/${id}/location`;
  return useGet(["organization-location", { id }], url);
};

export const useOrganizationLocationCreate = () => {
  return usePost(getOrgLocationUrl, {
    queriesToInvalidate: ["organization-location"],
  });
};

export const useOrganizationLocationUpdate = () => {
  return usePut(getOrgLocationUrl, {
    queriesToInvalidate: ["organization-location"],
  });
};

export const useLocationSearch = searchString => {
  const url = `${AUTH_BASE_URL}/location/lookup/${searchString}`;
  return useGet(
    ["location-search", { searchString }],
    url,
    notBlank(searchString),
  );
};

export const useBuyingGroups = () => {
  const url = `${AUTH_BASE_URL}/buying_groups`;
  return useGetAll("buying-groups", url);
};

export const useUsersWithRole = role => {
  const url = `${AUTH_BASE_URL}/roles/${role}/users`;
  return useGet(["users", { role }], url);
};

export const useUploadOrganizationLogo = () => {
  return useUpload(getOrgLogoUrl, {
    queriesToInvalidate: ["organization-profile", ["id"]],
  });
};

export const useDeleteOrganizationLogo = () => {
  return useDelete(getOrgLogoUrl, {
    queriesToInvalidate: ["organization-profile", ["id"]],
  });
};

export const useOrganizationKeystore = id => {
  const url = `${AUTH_BASE_URL}/organizations/${id}/secret_keystore`;
  return useGet(["organization-keystore", { orgId: id }], url);
};

export const useOrganizationKeystoreCreate = () => {
  return usePost(getOrgKeystoreUrl, {
    queriesToInvalidate: ["organization-keystore", ["orgId"]],
  });
};

export const useOrganizationKeystoreDelete = () => {
  return useDelete(getOrgKeystoreAccessKeyUrl, {
    queriesToInvalidate: ["organization-keystore", ["orgId"]],
  });
};

export const useBuyingGroupMembers = (id, page, limit) => {
  const offset = page * limit;
  const params = pickBy((val, _key) => notBlank(val), {
    offset,
    limit,
  });

  const queryParams = querystring.encode(params);
  const url = `${AUTH_BASE_URL}/buying_groups/${id}/members?${queryParams}`;
  return useGet(["buying-group-members", { url }], url);
};

export const useBuyingGroupMemberAdd = () => {
  return usePut(getBuyingGroupUrl, {
    queriesToInvalidate: ["buying-group-members"],
  });
};

export const useBuyingGroupMemberDelete = () => {
  return useDelete(getBuyingGroupUrl, {
    queriesToInvalidate: ["buying-group-members"],
  });
};

export const useActiveModule = id => {
  const currentOrgId = useCurrentOrgId();
  const url = getActiveModuleUrl({
    moduleId: id,
    orgId: currentOrgId,
  });
  return useGet(["active-module", { moduleId: id }], url);
};

export const useCreateActiveModule = () => {
  return usePut(getActiveModuleUrl, {
    queriesToInvalidate: ["admin-media"],
  });
};

export const useDeleteActiveModule = () => {
  return useDelete(getActiveModuleUrl, {
    queriesToInvalidate: ["admin-media"],
  });
};

export const useRequestCollectionMarc = () => {
  return usePost(getRequestCollectionMarcURL);
};

export const LibraryTypes = [
  {
    libraryTypeId: "3b16bd78-ff07-4743-89e2-ecdf3e523f94",
    name: "State Library",
  },
  { libraryTypeId: "43abea9d-0d18-4598-9dc7-8b2edbaa4610", name: "Public" },
  { libraryTypeId: "5a011119-5c36-4dcb-a8d4-e49887c6946f", name: "K-12" },
  { libraryTypeId: "85faf851-dd58-462c-9152-ce88d496d685", name: "Private" },
  { libraryTypeId: "8f057fc3-382b-4566-a2a4-aff44da7f3d9", name: "Curator" },
  { libraryTypeId: "983ee215-b21d-41dd-bcca-81a8d2575ba7", name: "Academic" },
  { libraryTypeId: "afe11bac-3a78-426a-b76f-e50d45162632", name: "Special" },
  { libraryTypeId: "cf3560d0-0501-4985-a7fe-bee5a22d057e", name: "Other" },
  {
    libraryTypeId: "df07efb3-048e-4be3-b289-e46f0f963d6b",
    name: "Consortium",
  },
  { libraryTypeId: "e68447c5-7136-4a02-97b8-2e253a7b186d", name: "Museum" },
];

export const getLibraryType = id =>
  find(propEq("libraryTypeId", id), LibraryTypes);
